// Features: M004-F0715, M004-F0720
// Tests: T-M004-0210, T-M004-0225
//
// The write engine for the customer ledger page's Move to invoice / Back to advice and Bill by hand actions (the amount
// edit of an Advice item is in the item drawer, WebUI #167, and is sent by LedgerQueueSections) (design M004-F0712-F0716 sections 4.1-4.7a, 4.6). It is deliberately NOT part of
// LedgerQueueSections' own runWrite: every request here goes to the Ledger only (design 4.1a: a move never waits on,
// is never gated by and never polls Core), so there is no Core read anywhere in this file.
//
// Rules implemented here (each has a test in LedgerQueueSections.moves.test.js):
//  * one Idempotency-Key per user INTENT (not per click), re-sent on a retry and across a step-up replay (4.2);
//  * a synchronous in-flight guard, so a double click is ONE request (4.2);
//  * CONFIRMED UI: nothing changes until the answer arrives; the page then renders from the answer (4.3);
//  * `moved:false` is never an error: a repeat is a neutral status, a condition opens the reason prompt or prints Core's
//    sentence (ACCT-TX-004/005/017);
//  * only declared refusals get copy (4.4); everything else falls back to the node's own words.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import StepUpDialog from '../auth/StepUpDialog';
import { ledgerAccountApi } from '../../services/api';
import { getStepUpChallengeFromError } from '../../utils/stepUp';
import { displayTxId, sameHandOff } from '../../utils/transactionId';
import { DEFAULT_RETRY_SECONDS, handRefusal, hasCondition, isTunnelTimeout, moveRefusal, normaliseEdit, normaliseMove, pendingAnswer } from '../../services/ledgerMovesAdapter';

const newKey = () => globalThis.crypto?.randomUUID?.() || `mv-${Date.now()}-${Math.random()}`;

export const MOVED_TO_DRAFT = 'Moved to the next invoice.';
export const MOVED_TO_ADVICE = 'Moved back to Advice.';
export const ALREADY_ON_DRAFT = 'Already on the next invoice.';
export const ALREADY_IN_ADVICE = 'Already in Advice.';
// Core #393: a command the Ledger is still working on (202 LEDGER_COMMAND_IN_PROGRESS, or 504 TUNNEL_NODE_TIMEOUT on the first
// request). Not an error: the button stays disabled, the queue is re-read after retry_after_seconds, at most this many times.
export const STILL_WORKING = 'Still working — the Ledger is taking longer than usual. Refreshing…';
export const CHECK_AGAIN = 'Still working — the Ledger is taking longer than usual. Check again shortly.';
export const PENDING_REFRESHES = 3;

// ML001-F0239: `enabled` is the money-action gate (canPerformMoneyActions). Default false: a caller that does not say
// it may act cannot move, change an amount or bill by hand, whatever a control or shortcut tries.
export default function useLedgerMoves({ orgId, userId, money, draft, advice, applyState, reload, enabled = false }) {
  const available = enabled === true && typeof ledgerAccountApi?.moveTransaction === 'function'
    && typeof ledgerAccountApi?.editAdviceAmount === 'function'
    && typeof ledgerAccountApi?.billByHand === 'function';
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [panel, setPanel] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const inFlight = useRef(false);
  const attempts = useRef(new Map());
  const pending = useRef(null);
  // Core #393: the command the Ledger is still working on ({ job, seconds, refreshes }). While set, nothing else is sent.
  const [waiting, setWaiting] = useState(null);
  const waitingRef = useRef(null);
  const latest = useRef({});
  latest.current = { draft, applyState, reload, money };

  const enterWaiting = useCallback((job, seconds) => {
    const next = { job, seconds, refreshes: 0 };
    waitingRef.current = next;
    setWaiting(next);
    setNotice({ scope: job.scope, tone: 'pending', text: STILL_WORKING });
  }, []);

  const keyFor = (intent) => {
    if (!attempts.current.has(intent)) attempts.current.set(intent, newKey());
    return attempts.current.get(intent);
  };

  const execute = useCallback(async (job) => {
    if (enabled !== true) return false; // ML001-F0239: no write leaves while the gate is closed
    // Synchronous guard: `busyId` only disables the buttons after a re-render, which a double click can beat.
    if (inFlight.current || waitingRef.current) return false;
    inFlight.current = true;
    setBusyId(job.id);
    setNotice(null);
    try {
      const response = await job.send(job.key);
      const wait = pendingAnswer(response);
      if (wait) {
        // Core #393: still working. The key is KEPT, so a later click of the same intent gets the real answer, never a second move.
        enterWaiting(job, wait.retryAfterSeconds);
        return false;
      }
      attempts.current.delete(job.intent);
      pending.current = null;
      job.onOk(response);
      return true;
    } catch (cause) {
      // Core #393: the first request timed out at the tunnel while the Ledger may still apply it. Pending, not an error.
      if (isTunnelTimeout(cause)) {
        enterWaiting(job, DEFAULT_RETRY_SECONDS);
        return false;
      }
      // Step-up is Core's 401 STEP_UP_REQUIRED; a 403 (incl. LEDGER_ACCOUNT_SAL_REQUIRED) is a refusal and never opens the dialog.
      const stepUp = Number(cause?.response?.status) === 403 ? null : getStepUpChallengeFromError(cause);
      if (stepUp?.id) {
        pending.current = job;
        setChallenge(stepUp);
        return false;
      }
      pending.current = null;
      // A definitive answer (any HTTP response) ends the intent; a network failure keeps the key for the retry.
      if (cause?.response) attempts.current.delete(job.intent);
      job.onErr(cause);
      return false;
    } finally {
      inFlight.current = false;
      setBusyId(null);
    }
  }, [enabled, enterWaiting]);

  const refresh = () => { try { const pendingRead = latest.current.reload?.(); if (pendingRead?.catch) pendingRead.catch(() => {}); } catch { /* the view stays as the answer left it */ } };

  // ---------------------------------------------------------------- move (to draft / to advice)
  const move = (item, to, reason = '') => {
    const handOffId = item.handOffId;
    const scope = to === 'draft' ? 'advice' : 'approval';
    const intent = `move|${handOffId}|${to}|${reason}`;
    return execute({
      id: handOffId, intent, key: keyFor(intent), scope, landOn: to,
      onLanded: () => setNotice({ scope, tone: 'ok', text: to === 'draft' ? MOVED_TO_DRAFT : MOVED_TO_ADVICE }),
      send: (key) => ledgerAccountApi.moveTransaction(orgId, userId, { handOffId, to, reason: reason || undefined }, key),
      onOk: (response) => {
        const result = normaliseMove(response, latest.current.money);
        if (result.moved) {
          // Confirmed: render from the answer, then a background re-read of the LEDGER queues only (4.1a.3).
          latest.current.applyState({ advice: result.advice, draft: result.draft });
          setPanel(null);
          setNotice({ scope, tone: 'ok', text: to === 'draft' ? MOVED_TO_DRAFT : MOVED_TO_ADVICE });
        } else if (hasCondition(result.conditions, 'reason_required')) {
          // Not done yet and never an error: ask for the reason (ACCT-TX-004/005/017).
          setNotice(null);
          setPanel({ kind: 'reason', id: handOffId, to, conditions: result.conditions });
        } else if (result.conditions.length) {
          setPanel(null);
          setNotice({ scope, tone: 'condition', text: '', conditions: result.conditions });
        } else {
          setPanel(null);
          setNotice({ scope, tone: 'neutral', text: to === 'draft' ? ALREADY_ON_DRAFT : ALREADY_IN_ADVICE });
        }
        if (result.moved || !result.conditions.length) refresh();
      },
      onErr: (cause) => {
        const refusal = moveRefusal(cause, handOffId);
        setNotice({ scope, tone: 'error', text: refusal.text });
        if (refusal.reload) refresh();
      },
    });
  };

  // ---------------------------------------------------------------- Bill by hand (ACCT-TX-014)
  const billByHand = (item, { description, amount, reason, correction = false }) => {
    const handOffId = item.handOffId;
    const intent = `hand|${handOffId}|${description}|${Math.round(amount * 100)}|${reason}|${correction}`;
    const alreadyThere = (latest.current.draft?.lines || []).some((line) => sameHandOff(line.handOffId, handOffId));
    const txId = displayTxId({ txnId: item.txId, handOffId });
    return execute({
      id: handOffId, intent, key: keyFor(intent), scope: 'advice', landOn: 'draft',
      onLanded: () => setNotice({ scope: 'advice', tone: 'ok', text: `Added to the next invoice${txId ? ` for ${txId}` : ''}.` }),
      send: (key) => ledgerAccountApi.billByHand(orgId, userId, { handOffId, description, amount, reason, correction }, key),
      onOk: (response) => {
        latest.current.applyState(normaliseEdit(response, latest.current.money));
        setPanel(null);
        setNotice({ scope: 'advice', tone: alreadyThere ? 'neutral' : 'ok', text: alreadyThere ? ALREADY_ON_DRAFT : `Added to the next invoice${txId ? ` for ${txId}` : ''}.` });
        refresh();
      },
      onErr: (cause) => {
        const refusal = handRefusal(cause, handOffId);
        setNotice({ scope: 'advice', tone: 'error', text: refusal.text });
        if (refusal.reload) refresh();
      },
    });
  };

  // Core #393: the queue re-read after each retry_after_seconds. Each cycle is one timer; the cycle ends when the item has
  // landed (the lists show it), or after PENDING_REFRESHES re-reads, with "Check again shortly" (never an error).
  useEffect(() => {
    if (!waiting) return undefined;
    const { job, seconds, refreshes } = waiting;
    const landed = job.landOn === 'advice'
      ? (advice || []).some((entry) => entry.handOffId === job.id)
      : (draft?.lines || []).some((line) => sameHandOff(line.handOffId, job.id));
    if (landed) {
      // The Ledger's answer is now known: forget the key, so a later move of this intent is a NEW command.
      attempts.current.delete(job.intent);
      waitingRef.current = null;
      setWaiting(null);
      job.onLanded();
      return undefined;
    }
    const timer = setTimeout(() => {
      if (refreshes >= PENDING_REFRESHES) {
        waitingRef.current = null;
        setWaiting(null);
        setNotice({ scope: job.scope, tone: 'pending', text: CHECK_AGAIN });
        return;
      }
      try { const read = latest.current.reload?.(); if (read?.catch) read.catch(() => {}); } catch { /* the view stays as it is */ }
      const next = { ...waiting, refreshes: refreshes + 1 };
      waitingRef.current = next;
      setWaiting(next);
    }, seconds * 1000);
    return () => clearTimeout(timer);
  }, [waiting, advice, draft]);

  const openPanel = (next) => { setNotice(null); setPanel(next); };
  const closePanel = () => setPanel(null);

  const dialog = (
    <StepUpDialog challenge={challenge} title="Verify invoice change" description="Confirm your identity before changing this customer's invoice."
      onComplete={() => { const job = pending.current; setChallenge(null); if (job) execute(job); }}
      onCancel={() => {
        const job = pending.current; pending.current = null; setChallenge(null);
        if (job) setNotice({ scope: job.intent.startsWith('move|') && job.intent.split('|')[2] === 'advice' ? 'approval' : 'advice', tone: 'neutral', text: job.intent.startsWith('move|') ? 'Verification was cancelled. Nothing was moved.' : 'Verification was cancelled. Nothing was changed.' });
      }} />
  );

  return {
    orgId,
    userId,
    available,
    busy: busyId !== null || waiting !== null,
    busyId: busyId ?? waiting?.job.id ?? null,
    notice,
    panel,
    openPanel,
    closePanel,
    move,
    billByHand,
    // A move needs only the Ledger's hand_off_id: Core's release / dispatch / acknowledgement state is never a precondition (4.1a.2).
    canMove: (item) => available && typeof item?.handOffId === 'string' && item.handOffId !== '',
    dialog,
  };
}
